// frontend/src/components/QRCodeDisplay.js
/**
 * QR Code Display Component
 * 
 * Displays QR code for an asset with:
 * - QR code image from backend endpoint
 * - Asset tag display
 * - Print functionality
 * - Loading and error states
 * 
 * Theme: Matches parent component
 */

import React, { useState, useEffect } from 'react';
import { QrCode, Printer, AlertCircle, RefreshCw, Loader } from 'lucide-react';
import api from '../services/api';

// Get API base URL - use relative path, axios will handle baseURL automatically
const getQRCodeUrl = (assetId) => {
  // Use relative path - the axios instance in api.js already has baseURL configured
  return `/asset-inventory/assets/${assetId}/qr-code/`;
};

const QRCodeDisplay = ({ assetId, assetTag, showPrintButton = true, className = '' }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!assetId) {
      setError('Asset ID is required');
      setIsLoading(false);
      return;
    }

    let currentBlobUrl = null;

    // Fetch QR code image with authentication
    const fetchQRCode = async () => {
      setIsLoading(true);
      setError(null);
      setImageError(false);

      // Cleanup previous blob URL if it exists
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
        currentBlobUrl = null;
      }

      try {
        const url = getQRCodeUrl(assetId);
        
        // Fetch image with axios to include authentication headers
        const response = await api.get(url, {
          responseType: 'blob', // Important: get binary data
        });

        // Create blob URL from the response
        const blob = new Blob([response.data], { type: 'image/png' });
        const blobUrl = URL.createObjectURL(blob);
        currentBlobUrl = blobUrl;
        
        setQrCodeUrl(blobUrl);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching QR code:', err);
        
        // Handle different error types
        if (err.response?.status === 403) {
          setError('You do not have permission to view this QR code');
        } else if (err.response?.status === 404) {
          setError('Asset not found');
        } else {
          setError('Failed to load QR code. Please try again.');
        }
        
        setIsLoading(false);
        setImageError(true);
      }
    };

    fetchQRCode();

    // Cleanup blob URL on unmount or when assetId changes
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
      // Also cleanup the state blob URL if it exists
      setQrCodeUrl(prevUrl => {
        if (prevUrl && prevUrl.startsWith('blob:')) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [assetId]);

  const handlePrint = () => {
    if (!qrCodeUrl) {
      alert('QR code is not loaded yet. Please wait and try again.');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print QR code');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${assetTag || 'Asset'}</title>
          <style>
            @media print {
              @page {
                margin: 10mm;
                size: auto;
              }
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              padding: 10px;
            }
            .qr-code {
              margin: 0 auto;
              padding: 5px;
            }
            .qr-code img {
              max-width: 100%;
              height: auto;
            }
            .error {
              color: red;
              padding: 20px;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="qr-code">
              <img src="${qrCodeUrl}" alt="QR Code for ${assetTag || 'Asset'}" onerror="this.style.display='none'; document.querySelector('.error').style.display='block';" />
              <div class="error" style="display:none;">
                Failed to load QR code image. Please try again.
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for image to load before printing
    printWindow.onload = () => {
      const img = printWindow.document.querySelector('img');
      if (img) {
        img.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
        img.onerror = () => {
          alert('Failed to load QR code image for printing. Please try again.');
        };
      } else {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    };
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    // Retry fetching the QR code
    setImageError(false);
    setIsLoading(true);
    setError(null);
    
    const fetchQRCode = async () => {
      try {
        const url = getQRCodeUrl(assetId);
        const response = await api.get(url, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'image/png' });
        const blobUrl = URL.createObjectURL(blob);
        
        setQrCodeUrl(blobUrl);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching QR code on retry:', err);
        if (err.response?.status === 403) {
          setError('You do not have permission to view this QR code');
        } else if (err.response?.status === 404) {
          setError('Asset not found');
        } else {
          setError('Failed to load QR code. Please try again.');
        }
        setIsLoading(false);
        setImageError(true);
      }
    };

    fetchQRCode();
  };

  if (error) {
    return (
      <div className={`bg-gray-50 rounded-xl p-3 ${className}`}>
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-medium">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-xl p-3 ${className}`}>
      <div className="flex items-center gap-4">
        {/* Left: label, help text, and print button */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <QrCode className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">QR Code</span>
            {showPrintButton && qrCodeUrl && !isLoading && !imageError && (
              <button
                onClick={handlePrint}
                className="ml-1 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                title="Print QR Code"
              >
                <Printer className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400">
            Scan with your mobile device to view asset details
          </p>
        </div>

        {/* Right: QR code image */}
        <div className="flex-shrink-0 w-32 h-32 flex items-center justify-center">
          {isLoading && !qrCodeUrl ? (
            <div className="flex flex-col items-center gap-2">
              <Loader className="w-6 h-6 text-gray-400 animate-spin" />
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          ) : imageError ? (
            <div className="flex flex-col items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <button
                onClick={handleRetry}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 underline"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          ) : qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt={`QR Code for ${assetTag || 'Asset'}`}
              className="w-32 h-32 object-contain"
              onError={handleImageError}
              onLoad={() => setIsLoading(false)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
